import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

const darkTheme = {
  colorScheme: 'dark',
  '--content-background': '#161a1f',
  '--content-text': '#f6f0e7',
  '--content-muted': 'rgba(246, 240, 231, 0.76)',
  '--content-soft': 'rgba(246, 240, 231, 0.58)',
  '--content-faint': 'rgba(246, 240, 231, 0.36)',
  '--content-border': 'rgba(246, 240, 231, 0.14)',
  '--content-surface': 'rgba(246, 240, 231, 0.07)',
  '--content-surface-hover': 'rgba(246, 240, 231, 0.13)',
  '--content-primary-background': '#f6f0e7',
  '--content-primary-text': '#161a1f',
  '--content-input': '#20262d',
  '--content-table': '#20262d',
  '--content-table-alt': '#242b33',
  '--content-accent': '#8bc6a8',
};

const lightTheme = {
  colorScheme: 'light',
  '--content-background': '#faf7ef',
  '--content-text': '#2d352f',
  '--content-muted': 'rgba(45, 53, 47, 0.7)',
  '--content-soft': 'rgba(45, 53, 47, 0.52)',
  '--content-faint': 'rgba(45, 53, 47, 0.34)',
  '--content-border': 'rgba(45, 53, 47, 0.13)',
  '--content-surface': 'rgba(255, 255, 255, 0.48)',
  '--content-surface-hover': 'rgba(45, 53, 47, 0.06)',
  '--content-primary-background': '#2d352f',
  '--content-primary-text': '#faf7ef',
  '--content-input': '#ffffff',
  '--content-table': '#ffffff',
  '--content-table-alt': '#eee7d8',
  '--content-accent': '#5f806d',
};

const navItems = [
  { to: '/about', href: '/#biography', label: 'Biography' },
  { to: '/books', label: 'Books' },
  {
    to: '/malamfamilyhistory',
    label: 'Malam Family History',
    match: ['/malamfamilyhistory', '/familytree', '/edwardmalam'],
    matchPrefix: ['/edwardmalam'],
    children: [
      { to: '/malamfamilyhistory', label: 'Origins' },
      { to: '/familytree', label: 'Family Tree' },
      { to: '/edwardmalam', label: 'Edward Malam, brewer', matchPrefix: ['/edwardmalam'] },
    ],
  },
  { to: '/contact', label: 'Contact' },
];

const mobileNavItems = [
  { to: '/', label: 'Home' },
  { to: '/about', href: '/#biography', label: 'Biography' },
  { to: '/books', label: 'Books' },
  { to: '/malamfamilyhistory', label: 'Malam Family History' },
  { to: '/familytree', label: 'Family Tree' },
  { to: '/edwardmalam', label: 'Edward Malam' },
  { to: '/edwardmalam/brewery', label: 'Edward Malam: Brewery' },
  { to: '/edwardmalam/family', label: 'Edward Malam: Family' },
  { to: '/edwardmalam/will', label: 'Edward Malam: Will' },
  { to: '/edwardmalam/legacy', label: 'Edward Malam: Legacy' },
  { to: '/contact', label: 'Contact' },
];

function isActive(item, pathname) {
  return (item.match || [item.to]).includes(pathname)
    || item.matchPrefix?.some((prefix) => (
      pathname === prefix || pathname.startsWith(`${prefix}/`)
    ));
}

function HomeIcon() {
  return (
    <svg className="content-home-icon" aria-hidden="true" viewBox="0 0 24 24">
      <path d="m3 10.75 9-7.5 9 7.5" />
      <path d="M5 10v10h14V10" />
      <path d="M9.5 20v-6h5v6" />
    </svg>
  );
}

function ThemeToggle({ isLightMode, onToggle, className = '' }) {
  return (
    <button
      type="button"
      aria-label={isLightMode ? 'Switch to dark mode' : 'Switch to light mode'}
      title={isLightMode ? 'Switch to dark mode' : 'Switch to light mode'}
      onClick={onToggle}
      className={`content-theme-toggle ${className}`}
    >
      {isLightMode ? (
        <svg aria-hidden="true" viewBox="0 0 24 24">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z" />
        </svg>
      ) : (
        <svg aria-hidden="true" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2" />
          <path d="M12 20v2" />
          <path d="m4.93 4.93 1.42 1.42" />
          <path d="m17.66 17.66 1.41 1.41" />
          <path d="M2 12h2" />
          <path d="M20 12h2" />
          <path d="m6.34 17.66-1.41 1.41" />
          <path d="m19.07 4.93-1.41 1.42" />
        </svg>
      )}
    </button>
  );
}

function ContentLayout({
  title,
  titleSuffix,
  intro,
  showIntro = false,
  hideHeroBanner = false,
  heroImage = '/john.png',
  heroAlt = 'John Malam',
  heroPosition = 'center 46%',
  navigationPath,
  className = '',
  sectionNav = [],
  children,
}) {
  const location = useLocation();
  const sidebarRef = useRef(null);
  const frameRef = useRef(null);
  const normalizedHeroImage = heroImage.startsWith('/')
    ? heroImage
    : `/${heroImage}`;
  const heroImageSrc = heroImage.startsWith('http')
    ? heroImage
    : `${process.env.PUBLIC_URL}${normalizedHeroImage}`;
  const activePath = navigationPath || location.pathname;
  const mobileNavValue = mobileNavItems.some((item) => item.to === activePath)
    ? activePath
    : '/edwardmalam';
  const isEdwardPage = location.pathname === '/edwardmalam'
    || location.pathname.startsWith('/edwardmalam/');
  const shouldNestSectionNav = sectionNav.length > 0 && isEdwardPage;
  const [isLightMode, setIsLightMode] = useState(() => {
    if (typeof window === 'undefined') {
      return true;
    }

    const savedTheme = window.localStorage.getItem('johnmalam-content-theme');

    if (savedTheme) {
      return savedTheme === 'light';
    }

    return !window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    window.localStorage.setItem(
      'johnmalam-content-theme',
      isLightMode ? 'light' : 'dark',
    );
  }, [isLightMode]);

  useLayoutEffect(() => {
    const sidebar = sidebarRef.current;
    const frame = frameRef.current;

    if (!sidebar || !frame || typeof window === 'undefined') {
      return undefined;
    }

    let frameId = 0;

    const updateSidebarLayout = () => {
      frameId = 0;
      const minimumTopOffset = 32;
      const frameRect = frame.getBoundingClientRect();
      const frameStyles = window.getComputedStyle(frame);
      const framePaddingTop = Number.parseFloat(frameStyles.paddingTop) || 0;
      const framePaddingLeft = Number.parseFloat(frameStyles.paddingLeft) || 0;
      const sidebarTop = Math.max(
        minimumTopOffset,
        frameRect.top + framePaddingTop,
      );
      const sidebarLeft = frameRect.left + framePaddingLeft;

      sidebar.style.setProperty('--content-sidebar-viewport-top', `${sidebarTop}px`);
      sidebar.style.setProperty('--content-sidebar-left', `${sidebarLeft}px`);
    };

    const scheduleSidebarLayoutUpdate = () => {
      if (frameId) {
        return;
      }

      frameId = window.requestAnimationFrame(updateSidebarLayout);
    };

    updateSidebarLayout();
    window.addEventListener('scroll', scheduleSidebarLayoutUpdate, { passive: true });
    window.addEventListener('resize', scheduleSidebarLayoutUpdate);

    return () => {
      window.removeEventListener('scroll', scheduleSidebarLayoutUpdate);
      window.removeEventListener('resize', scheduleSidebarLayoutUpdate);

      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [location.pathname, sectionNav.length]);

  const scrollToSection = (event, id) => {
    const target = document.getElementById(id);

    if (!target) {
      return;
    }

    event.preventDefault();
    window.history.pushState(null, '', `${location.pathname}${location.search}#${id}`);
    target.scrollIntoView({ block: 'start' });
  };

  const changeMobilePage = (event) => {
    const selectedItem = mobileNavItems.find((item) => item.to === event.currentTarget.value);

    window.location.assign(selectedItem?.href || event.currentTarget.value);
  };

  return (
    <div
      className={[
        'content-shell',
        hideHeroBanner ? 'content-shell-no-hero' : '',
        className,
      ].filter(Boolean).join(' ')}
      style={isLightMode ? lightTheme : darkTheme}
    >
      {!hideHeroBanner && (
        <header className="content-hero-banner">
          <img
            src={heroImageSrc}
            alt={heroAlt}
            style={{ objectPosition: heroPosition }}
          />
        </header>
      )}

      <div className="content-mobile-nav">
        <a href="/" className="content-logo-link" aria-label="John Malam home">
          <HomeIcon />
        </a>
        <label htmlFor="content-page-nav" className="sr-only">
          Navigate site
        </label>
        <div className="content-mobile-select-wrap">
          <select
            id="content-page-nav"
            value={mobileNavValue}
            onChange={changeMobilePage}
            className="content-mobile-select"
          >
            {mobileNavItems.map((item) => (
              <option key={item.to} value={item.to}>
                {item.label}
              </option>
            ))}
          </select>
          <svg aria-hidden="true" viewBox="0 0 20 20">
            <path d="m5 7.5 5 5 5-5" />
          </svg>
        </div>
        <ThemeToggle
          isLightMode={isLightMode}
          onToggle={() => setIsLightMode((current) => !current)}
        />
      </div>

      <main className="content-main">
        <div className="content-frame" ref={frameRef}>
          <aside className="content-sidebar" ref={sidebarRef}>
            <div className="content-sidebar-inner">
              <div className="content-sidebar-scroll">
                <a href="/" className="content-brand" aria-label="John Malam home">
                  <span className="content-brand-name">John Malam</span>
                </a>

                <nav aria-label="Content navigation" className="content-side-nav">
                  <ol>
                    {navItems.map((item) => (
                      <li key={item.to}>
                        <a
                          href={item.href || item.to}
                          className={isActive(item, activePath) ? 'active' : ''}
                          aria-current={isActive(item, activePath) ? 'page' : undefined}
                        >
                          {item.label}
                        </a>
                        {item.children && (
                          <ol className="content-subnav">
                            {item.children.map((child) => (
                              <li key={child.to}>
                                <a
                                  href={child.to}
                                  className={isActive(child, activePath) ? 'active' : ''}
                                  aria-current={isActive(child, activePath) ? 'page' : undefined}
                                >
                                  {child.label}
                                </a>
                                {shouldNestSectionNav && child.to === '/edwardmalam' && (
                                  <ol className="content-subnav content-subnav-nested">
                                    {sectionNav.map((sectionItem) => (
                                      <li key={sectionItem.id}>
                                        {sectionItem.to ? (
                                          <a
                                            href={sectionItem.to}
                                            className={isActive(sectionItem, activePath) ? 'active' : ''}
                                            aria-current={isActive(sectionItem, activePath) ? 'page' : undefined}
                                          >
                                            {sectionItem.label}
                                          </a>
                                        ) : (
                                          <a
                                            href={`#${sectionItem.id}`}
                                            onClick={(event) => scrollToSection(event, sectionItem.id)}
                                          >
                                            {sectionItem.label}
                                          </a>
                                        )}
                                      </li>
                                    ))}
                                  </ol>
                                )}
                              </li>
                            ))}
                          </ol>
                        )}
                      </li>
                    ))}
                  </ol>
                </nav>

                {sectionNav.length > 0 && !shouldNestSectionNav && (
                  <nav aria-label={`${title} sections`} className="content-section-nav">
                    <ol>
                      {sectionNav.map((item) => (
                        <li key={item.id}>
                          {item.to ? (
                            <a
                              href={item.to}
                              className={isActive(item, activePath) ? 'active' : ''}
                              aria-current={isActive(item, activePath) ? 'page' : undefined}
                            >
                              {item.label}
                            </a>
                          ) : (
                            <a
                              href={`#${item.id}`}
                              onClick={(event) => scrollToSection(event, item.id)}
                            >
                              {item.label}
                            </a>
                          )}
                        </li>
                      ))}
                    </ol>
                  </nav>
                )}
              </div>

              <ThemeToggle
                isLightMode={isLightMode}
                onToggle={() => setIsLightMode((current) => !current)}
                className="content-sidebar-toggle"
              />
            </div>
          </aside>

          <article className="content-panel">
            <header className="content-page-header">
              <h1>
                {title}
                {titleSuffix && (
                  <span className="content-title-suffix">{titleSuffix}</span>
                )}
              </h1>
              {showIntro && intro && <p className="content-intro">{intro}</p>}
            </header>
            {children}
          </article>
        </div>
      </main>
    </div>
  );
}

export default ContentLayout;
